// src/components/AppLauncher/hooks/useAppLoader.js

import { useState, useEffect, useCallback } from 'react';
import { pluginApi } from '../../../utils/api';
import { ASSETS } from '../../../constants';

export const useAppLoader = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPluginVersion = async (pluginId, branch, type) => {
    if (!branch) return null;
    try {
      const module = await import(`../../../plugins/${type.toLowerCase()}/${pluginId}/src/index.js`);
      return {
        component: module.default,
        version: type,
        metadata: module.metadata
      };
    } catch {
      return null;
    }
  };

  const loadThumbnail = async (pluginId, thumbnailUrl) => {
    try {
      let iconUrl = ASSETS.IMAGES.PLACEHOLDER;
      if (thumbnailUrl) {
        const thumbnail = await pluginApi.getThumbnail(pluginId);
        if (thumbnail) {
          iconUrl = URL.createObjectURL(thumbnail);
        }
      }
      return iconUrl;
    } catch {
      return ASSETS.IMAGES.PLACEHOLDER;
    }
  };

  const createWebApp = async (plugin) => {
    const versions = {
      production: await loadPluginVersion(plugin.plugin_id, plugin.main_branch, 'production'),
      beta: await loadPluginVersion(plugin.plugin_id, plugin.beta_branch, 'beta')
    };

    if (!versions.production && !versions.beta) {
      return null;
    }

    const defaultVersion = versions.production ? 'production' : 'beta';
    const icon = await loadThumbnail(plugin.plugin_id, plugin.thumbnail_url);

    return {
      id: plugin.plugin_id.toString(),
      name: plugin.name || `Plugin ${plugin.plugin_id}`,
      description: plugin.description || 'No description available',
      author: plugin.author,
      type: 'internal',
      plugin_type: 'WEB',
      enabled: true,
      versions,
      selectedVersion: defaultVersion,
      display_mode: (plugin.display_mode || 'windowed').toLowerCase(),
      url: plugin.url || '',
      wikiUrl: plugin.wiki_url || '',
      main_branch: plugin.main_branch,
      beta_branch: plugin.beta_branch,
      category: plugin.category,
      display_order: plugin.display_order || 0,
      space_ids: plugin.space_ids || [],  // Space assignments
      icon,
      getComponent() {
        const version = this.selectedVersion;
        const versionData = this.versions[version];
        return versionData?.component;
      },
      metadata: { ...plugin }
    };
  };

  const createLinkApp = async (plugin) => {
    const icon = await loadThumbnail(plugin.plugin_id, plugin.thumbnail_url);

    return {
      id: plugin.plugin_id.toString(),
      name: plugin.name || `Plugin ${plugin.plugin_id}`,
      description: plugin.description || 'No description available',
      author: plugin.author,
      type: 'external',
      plugin_type: 'LINK',
      enabled: true,
      url: plugin.url || '',
      wikiUrl: plugin.wiki_url || '',
      category: plugin.category,
      display_order: plugin.display_order || 0,
      space_ids: plugin.space_ids || [],  // Space assignments
      icon
    };
  };

  const loadApps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const plugins = await pluginApi.list();
      const enabledPlugins = plugins.filter(p => p.enabled);

      const loadedApps = await Promise.all(enabledPlugins.map(async plugin => {
        if (plugin.plugin_type === 'WEB') {
          return await createWebApp(plugin);
        } else if (plugin.plugin_type === 'LINK') {
          return await createLinkApp(plugin);
        }
        return null;
      }));

      const validApps = loadedApps.filter(Boolean);
      setApps(validApps);
    } catch (err) {
      setError(err.message || 'Failed to load applications');
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  return { apps, loading, error, loadApps };
};
